import { useQuery } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Stack } from '@mui/material';

import Api from '@barboxx/api';

import { LocalPlaceTable } from '../../components/LocalPlaceTable';
import { SearchMap, SearchMapRefProps } from '../../components/SearchMap';

interface Props {
    onBusinessSelect: (data: google.maps.places.PlaceResult) => void;
    center?: google.maps.LatLngLiteral;
}

export interface NewPlaceMapRefProps {
    reset: () => void;
}

export const NewPlaceMap = forwardRef<NewPlaceMapRefProps, Props>(
    function NewPlaceMap({ onBusinessSelect, center }, ref) {
        const navigate = useNavigate();
        const mapRef = useRef<SearchMapRefProps | null>(null);
        const [currentLocation, setCurrentLocation] = useState<
            google.maps.LatLngLiteral | undefined
        >();

        useImperativeHandle(ref, () => ({
            reset,
        }));

        const location = currentLocation
            ? new google.maps.LatLng(currentLocation).toUrlValue()
            : undefined;

        const possiblePlacesQuery = useQuery({
            queryKey: ['places', location],
            queryFn: () => {
                return Api.Places.getPlaces({
                    location,
                    size: 10,
                    page: 0,
                });
            },
            enabled: !!location,
        });

        const reset = () => {
            mapRef.current?.clear();
            possiblePlacesQuery.refetch();
        };

        const handleSearchForPossiblePlaces = (
            latLng: google.maps.LatLngLiteral
        ) => {
            setCurrentLocation(latLng);
        };

        const handleNavigateToPlace = (id: string) => {
            navigate(`/places/${id}`);
        };

        return (
            <Stack>
                <SearchMap
                    possiblePlaces={possiblePlacesQuery.data?.data?.results}
                    searchForPossiblePlaces={handleSearchForPossiblePlaces}
                    onBusinessSelect={onBusinessSelect}
                    onSelectPlace={handleNavigateToPlace}
                    center={center}
                    ref={mapRef}
                />
                {currentLocation && (
                    <LocalPlaceTable
                        possiblePlaces={possiblePlacesQuery}
                        onSelectPlace={handleNavigateToPlace}
                    />
                )}
            </Stack>
        );
    }
);
